import React, { useState } from "react";
import "./MergeGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { readBestScore, recordScore, mergeBestScoreKey } from "../Quiz/bestScore.ts";
import { track } from "../../Shared/analytics.ts";
import { MergeGameBoard } from "./MergeGameBoard.tsx";
import { MergeGameSummary } from "./MergeGameSummary.tsx";
import { MERGE_LEVELS, MergeLevel, MergeResult } from "./mergeGame.ts";
import { readMergeLevel, saveMergeLevel } from "./mergeStore.ts";
import { mergeGameEventParams } from "./gameEventParams.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface MergeGamePageProps {
    slug: string;
    /** The route's `quiz`: the operation the calmer quiz is found by, since merging multiplies (ADR 0128 §5). */
    operation: Operations;
}

interface Summary {
    result: MergeResult;
    best: number | null;
    isNewBest: boolean;
}

/** What each grade's end screen sends the child to practise, by slug (ADR 0128 §7). */
export const PRACTICE: Record<MergeLevel, string[]> = {
    g3: ["/multiplication-worksheets", "/multiplication-word-problems"],
    g4: ["/factors-worksheets", "/multiples-worksheets"],
    g5: ["/factors-worksheets", "/multiples-worksheets", "/prime-and-composite-numbers-worksheets"],
    g6: [
        "/prime-and-composite-numbers-worksheets",
        "/greatest-common-factor-worksheets",
        "/least-common-multiple-worksheets",
    ],
    g7: [
        "/greatest-common-factor-worksheets",
        "/least-common-multiple-worksheets",
        "/prime-and-composite-numbers-worksheets",
    ],
    g8: ["/exponents-worksheets", "/exponent-rules-worksheets", "/prime-and-composite-numbers-worksheets"],
};

/** Each grade's name. `labelKey` so StringKeys.test.js sees the keys, as ShopGamePage's levels do. */
const LEVEL_LABELS: Record<MergeLevel, { labelKey: StringKey }> = {
    g3: { labelKey: "flashCards.mergeLevelG3" },
    g4: { labelKey: "flashCards.mergeLevelG4" },
    g5: { labelKey: "flashCards.mergeLevelG5" },
    g6: { labelKey: "flashCards.mergeLevelG6" },
    g7: { labelKey: "flashCards.mergeLevelG7" },
    g8: { labelKey: "flashCards.mergeLevelG8" },
};

/**
 * Factor Merge (ADR 0128): a keyword landing page around a 4 x 4 board of
 * numbered tiles, with a button for each grade, 3 to 8 (ADR 0128 §7).
 * A quiz route carrying `game: "merge"`; its `quiz` is only the operation the
 * link to the calmer quiz is found by, since the game has no queue, picker,
 * settings or missed facts to wire.
 */
export function MergeGamePage({ slug, operation }: MergeGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [level, setLevel] = useState<MergeLevel>(readMergeLevel);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [bests, setBests] = useState<Record<MergeLevel, number | null>>(
        () => Object.fromEntries(MERGE_LEVELS.map((grade) => [grade, readBestScore(mergeBestScoreKey(grade))])) as Record<MergeLevel, number | null>
    );

    const plainQuiz = quizRoutes().find((route) => route.quiz === operation && !route.game);
    const practice = PRACTICE[level];
    const best = bests[level];

    function choose(next: MergeLevel) {
        if (next === level) {
            return;
        }
        setLevel(next);
        saveMergeLevel(next);
        setSummary(null);
        setRunId((id) => id + 1);
    }

    function handleFinish(result: MergeResult) {
        const key = mergeBestScoreKey(result.level);
        const previous = readBestScore(key);
        const recorded = recordScore(key, result.targetsMade);
        // After the store, as the quiz does: analytics never costs a best score.
        track("quiz_completed", mergeGameEventParams(result));
        setBests((all) => ({ ...all, [result.level]: recorded > 0 ? recorded : null }));
        setSummary({
            result,
            best: recorded > 0 ? recorded : null,
            isNewBest: previous !== null ? result.targetsMade > previous : result.targetsMade > 0,
        });
    }

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="mergeTool">
                <p className="mergeHow">{t("flashCards.mergeHowToPlay")}</p>

                <div className="mergeLevels" role="group" aria-label={t("flashCards.mergeLevelsLabel")}>
                    {MERGE_LEVELS.map((option) => (
                        <button
                            key={option}
                            type="button"
                            className="mergeLevel"
                            aria-pressed={option === level}
                            onClick={() => choose(option)}
                        >
                            {t(LEVEL_LABELS[option].labelKey)}
                        </button>
                    ))}
                </div>

                <p className="mergeBest">{best !== null ? format(t("flashCards.yourBestTemplate"), { n: best }) : ""}</p>

                <MergeGameBoard key={`${level}-${runId}`} level={level} onFinish={handleFinish} />

                {summary && (
                    <MergeGameSummary
                        result={summary.result}
                        best={summary.best}
                        isNewBest={summary.isNewBest}
                        practiceSlugs={practice}
                        onPlayAgain={() => {
                            setSummary(null);
                            setRunId((id) => id + 1);
                        }}
                    />
                )}

                {plainQuiz && (
                    <p className="mergeCalmer">
                        {t("flashCards.mergeCalmerPrefix")}
                        <a href={plainQuiz.slug}>{t("flashCards.mergeCalmerLink")}</a>
                        {t("flashCards.mergeCalmerSuffix")}
                    </p>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default MergeGamePage;
